/**
 * CourseDateService
 * Service for managing course intake dates
 */
import { BaseService } from '../base/BaseService';
/**
 * Service for course intake date operations (enriched views per date)
 */
export class CourseDateService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? CourseDateService.DEFAULT_SERVICE_PATH,
            basePath: CourseDateService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    /**
     * Get a course date view by ID
     * @param id - The course date ID
     * @returns Course date model view
     */
    async getById(id) {
        const response = await this.getByIdRaw(id);
        if (!response.data) {
            throw new Error(`Course date with ID ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get a course date view by ID with full response
     * @param id - The course date ID
     * @returns ResponseData with course date model view
     */
    getByIdRaw(id) {
        const url = this.buildUrl(String(id));
        return this.http.get(url);
    }
    /**
     * Get all intake dates for a course
     * @param courseId - The course ID
     * @returns Array of course date model views
     */
    async byCourse(courseId) {
        const response = await this.byCourseRaw(courseId);
        return response.data ?? [];
    }
    /**
     * Get all intake dates for a course with full response
     * @param courseId - The course ID
     * @returns ResponseData with array of course date model views
     */
    byCourseRaw(courseId) {
        const url = this.buildUrl(`by-course/${courseId}`);
        return this.http.get(url);
    }
    /**
     * Create a course intake date
     * @param data - Create payload
     * @returns The created course date
     */
    async create(data) {
        const response = await this.createRaw(data);
        if (!response.data) {
            throw new Error('Failed to create course date');
        }
        return response.data;
    }
    /**
     * Create a course intake date with full response
     * @param data - Create payload
     * @returns ResponseData with the created course date
     */
    createRaw(data) {
        const url = this.buildUrl();
        return this.http.post(url, data);
    }
    /**
     * Update a course intake date
     * @param id - The course date ID
     * @param data - Update payload
     * @returns The updated course date
     */
    async update(id, data) {
        const response = await this.updateRaw(id, data);
        if (!response.data) {
            throw new Error(`Failed to update course date with ID ${id}`);
        }
        return response.data;
    }
    /**
     * Update a course intake date with full response
     * @param id - The course date ID
     * @param data - Update payload
     * @returns ResponseData with the updated course date
     */
    updateRaw(id, data) {
        const url = this.buildUrl(String(id));
        return this.http.put(url, data);
    }
    /**
     * Delete a course intake date
     * @param id - The course date ID
     * @returns True if deleted successfully
     */
    async delete(id) {
        const response = await this.deleteRaw(id);
        return response.success;
    }
    /**
     * Delete a course intake date with full response
     * @param id - The course date ID
     * @returns ResponseData (success/fail)
     */
    deleteRaw(id) {
        const url = this.buildUrl(String(id));
        return this.http.delete(url);
    }
}
CourseDateService.DEFAULT_SERVICE_PATH = 'ac-courses';
CourseDateService.BASE_PATH = 'api/course-dates';
//# sourceMappingURL=CourseDateService.js.map